import { DEVICE_INVENTORY_FIELD_IDS } from '@/features/it-operation-core';
import { putOperationPackageInstanceTask, type OperationPackageInstanceTaskCompleteBody } from '@/features/operation-instances/api';
import { apiClient } from '@/lib/api/client';
import { getDataOrThrowApiError } from '@/lib/api/error';
import { orpcClient } from '@/lib/api/orpc/client';
import type { ListDeviceInventoryPatternGenerationsOutput } from '@/lib/api/orpc/contracts/it-operation-core-device-inventory';
import type { BulkCompleteTasksRequest, BulkCompleteTasksResponse, GetProgressResponse, GetSummaryResponse, InstancesDetailQuery, InstancesDetailResponse, ListInstanceTagSummariesResult, ListInstancesV1Query, ListInstancesV1Response, ListSelfReportTargetsQuery, ListSelfReportTargetsResponse, ListSelfReportsQuery, ListSelfReportsResponse, ReportProxyRequest, SaveFormOperationPackageInstanceTaskParams, SaveFormOperationPackageInstanceTaskPayload, SaveFormOperationPackageInstanceTaskResponse } from './type';

/** デバイス棚卸しは IT Operation Core 固定 */
const DEVICE_INVENTORY_OWNER_PRODUCT = 'IT_OPERATION_CORE' as const;

/**
 * デバイス棚卸し一覧の固まり（タグ）サマリを取得（サーバー集計・固まり単位ページング）。
 *
 * `GET /v2/boe/packages/{ownerProduct}/instance-tags` を使う。
 * 対象インスタンスを全件取得してクライアント側で畳み込む方式（行単位ページングのため
 * 固まりがページ境界で割れる・件数増で重い）を廃し、サーバー側 GROUP BY tag の
 * 進捗集計をそのまま並べる。並びはサーバー固定（anchorDate 昇順 → tagId 昇順）。
 */
export async function listInstanceTagSummaries(args: {
  patternId: string;
  page?: number;
  pageSize?: number;
}): Promise<ListInstanceTagSummariesResult> {
  const page = args.page ?? 1;
  const pageSize = args.pageSize ?? 50;
  const res = await apiClient.GET('/v2/boe/packages/{ownerProduct}/instance-tags', {
    params: {
      path: {
        ownerProduct: DEVICE_INVENTORY_OWNER_PRODUCT
      },
      query: {
        patternId: args.patternId,
        page,
        pageSize
      }
    }
  });
  const data = getDataOrThrowApiError(res);
  return {
    items: data.items.map(item => ({
      id: item.id,
      name: item.name,
      anchorDate: item.anchorDate,
      totalCount: item.totalCount,
      completedCount: item.completedCount
    })),
    count: data.count,
    maxPage: data.maxPage,
    page: data.page
  };
}

/**
 * 起動 API のレスポンス。
 *
 * TODO: `lib/api/schema.d.ts`（自動生成）が未再生成のため手書きしている
 * （`Response_4d61d8a1` に `tagId` が無い）。`pnpm gen-api` 後は削除できる。
 */
type StartDeviceInventoryResult = {
  anchorDate: string;
  operationPackageInstanceIds: string[];
  targetDeviceCount: number;
  /**
   * 起動した全 Instance に付与した束ね（タグ）の ID。v2 パターンのみ（v1 は null）。
   * 起動後に束ね確認画面（device-inventory-confirmation）へ遷移するために使う。
   */
  tagId: string | null;
};

/**
 * デバイス棚卸しパッケージを開始（target-device フローから呼び出し）
 *
 * `payload.tagName` は BE で必須（`@MaxLength(200)`）。このタグ名が一覧の
 * グループ見出しになり、同名タグは同じ束ねに合流する。
 *
 * ⚠️ `tagName` は v2 パターンでは必須 / v1 パターンでは送らない（optional）。
 * BE 側が世代（generation）に応じて必須可否を分岐しているため（Task 5）。
 *
 * TODO: `payload.tagName` は BE で追加済みだが `lib/api/schema.d.ts`（自動生成）は
 * 未再生成。`pnpm gen-api` 後は生成された型がそのまま効くため、この引数型と
 * `StartDeviceInventoryResult` へのキャストは削除できる。
 */
export async function startDeviceInventory(body: {
  patternId: string;
  payload: {
    anchorDate: string;
    deviceIds: string[];
    tagName?: string;
  };
}): Promise<StartDeviceInventoryResult> {
  const res = await apiClient.POST('/v2/it-operation-core/device-inventory', {
    body: {
      operationPackagePatternId: body.patternId,
      payload: body.payload
    }
  });
  return getDataOrThrowApiError(res) as StartDeviceInventoryResult;
}

/** デバイス棚卸しパターンの世代一覧（v1: 旧 requestId 軸画面 / v2: 束ね画面 のルーティング用） */
export const getDeviceInventoryPatternGenerations = (): Promise<ListDeviceInventoryPatternGenerationsOutput> => orpcClient.itOperationCoreDeviceInventory.patternGenerations({});

// ============================================================================
// ⚠️ v1 専用（凍結）。v1 退役時に削除
//
// merge-base（be2a51ad2b233ae01066cd4da5050a4fb13026a5）の
// `DeviceInventoryOperation/query/api.ts` から復元。Task 12/13 の旧画面（v1）が import する。
// 旧 `listInstances` は当時の v2 一覧と名前が衝突したため `listInstancesV1`
// にリネーム。v2 側は #4483 でタグサマリ版に置き換わり削除済み。
// ============================================================================

/**
 * デバイス棚卸しパッケージインスタンス一覧を取得（旧 requestId 軸 API）
 */
export async function listInstancesV1(query: ListInstancesV1Query): Promise<ListInstancesV1Response> {
  const res = await apiClient.GET('/v2/it-operation-core/device-inventory', {
    params: {
      query
    }
  });
  return getDataOrThrowApiError(res);
}

/**
 * インスタンス詳細を取得
 * 特定のrequestIdを持つデバイス棚卸しパッケージのインスタンス詳細を取得します。
 * 同一requestIdの複数インスタンスを集約したデータを返します。
 */
export async function getInstancesDetail(patternId: string, query: InstancesDetailQuery): Promise<InstancesDetailResponse> {
  const res = await apiClient.GET('/v2/it-operation-core/device-inventory/{patternId}/detail', {
    params: {
      path: {
        patternId
      },
      query
    }
  });
  return getDataOrThrowApiError(res);
}
export async function getProgressByRequestId(requestId: string): Promise<GetProgressResponse> {
  const res = await apiClient.GET('/v2/it-operation-core/device-inventory/{requestId}/progress', {
    params: {
      path: {
        requestId
      }
    }
  });
  return getDataOrThrowApiError(res);
}
export async function getSummary(requestId: string): Promise<GetSummaryResponse> {
  const res = await apiClient.GET('/v2/it-operation-core/device-inventory/{requestId}/summary', {
    params: {
      path: {
        requestId
      }
    }
  });
  return getDataOrThrowApiError(res);
}

/**
 * デバイス棚卸しセルフレポート対象デバイス一覧取得
 */
export async function listSelfReportTargets(query: ListSelfReportTargetsQuery): Promise<ListSelfReportTargetsResponse> {
  const res = await apiClient.GET('/v2/it-operation-core/device-inventory/self-report-targets', {
    params: {
      query
    }
  });
  return getDataOrThrowApiError(res);
}

/**
 * デバイス棚卸しセルフレポート結果一覧を取得
 * エンドポイント: /v2/it-operation-core/device-inventory/self-reports
 */
export async function listSelfReports(query: ListSelfReportsQuery): Promise<ListSelfReportsResponse> {
  const res = await apiClient.GET('/v2/it-operation-core/device-inventory/self-reports', {
    params: {
      query
    }
  });
  return getDataOrThrowApiError(res);
}

/**
 * レポート代行を実行
 */
export async function executeReportProxy(request: ReportProxyRequest) {
  const {
    deviceId,
    taskInstanceId,
    packageInstanceId,
    deviceCondition,
    memo,
    reportedBy,
    deviceTag
  } = request;

  // multipart/form-dataのフィールド名は動的な形式
  // パターン: ^[a-zA-Z]+\.[0-9]+\.[a-zA-Z]+\.[0-9]+\.[a-zA-Z]+$
  // 実際のフォーム構造に合わせてフィールド名を構築
  const formPayload: SaveFormOperationPackageInstanceTaskPayload = {
    [DEVICE_INVENTORY_FIELD_IDS.v1.DEVICE_ID]: deviceId,
    [DEVICE_INVENTORY_FIELD_IDS.v1.DEVICE_TAG]: deviceTag || '',
    [DEVICE_INVENTORY_FIELD_IDS.v1.VERIFICATION_METHOD]: request.verificationMethod || 'REPRESENTATIVE_BY_STAFF',
    [DEVICE_INVENTORY_FIELD_IDS.v1.PLACEMENT_VERIFICATION_STATUS]: request.placementVerificationStatus,
    [DEVICE_INVENTORY_FIELD_IDS.v1.INVENTORY_REPORTER]: reportedBy,
    [DEVICE_INVENTORY_FIELD_IDS.v1.INVENTORY_MEMO]: memo || '',
    ...(deviceCondition && {
      [DEVICE_INVENTORY_FIELD_IDS.v1.INVENTORY_DEVICE_CONDITION]: deviceCondition
    })
  };
  await saveFormOperationPackageInstanceTask({
    instanceId: packageInstanceId,
    taskId: taskInstanceId
  }, formPayload);

  // APIレスポンスから更新されたデバイス情報を構築
  // 実際のAPIレスポンスに応じて調整が必要
  const updatedDevice = {
    packageInstanceId,
    taskInstanceId,
    taskStatus: 'InProgress' as const,
    deviceId,
    deviceName: request.deviceName || 'Unknown Device',
    deviceTag: request.deviceTag,
    deviceImageUrl: request.deviceImageUrl,
    assigneeType: 'assigned' as const,
    status: deviceCondition ?? null,
    reportStatus: 'REPORTED' as const,
    selfReportResult: {
      reportedAt: new Date().toISOString(),
      reportedBy,
      deviceCondition: deviceCondition ?? null,
      reportResult: request.placementVerificationStatus,
      memo
    },
    lastUpdatedAt: new Date().toISOString()
  };
  return {
    success: true,
    updatedDevice
  };
}

/**
 * 複数タスクの一括完了処理
 */
export async function bulkCompleteTasks(request: BulkCompleteTasksRequest): Promise<BulkCompleteTasksResponse> {
  const res = await apiClient.POST('/v2/it-operation-core/operation-instances/tasks/bulk-complete', {
    body: request
  });
  return getDataOrThrowApiError(res);
}

/**
 * 対応メモを更新
 */
export async function updateActionMemo(deviceOperationTaskId: string, actionMemo: string | null): Promise<void> {
  const res = await apiClient.PATCH('/v2/it-operation-core/device-inventory/action-memo/{deviceOperationTaskId}', {
    params: {
      path: {
        deviceOperationTaskId
      }
    },
    body: {
      actionMemo
    }
  });
  getDataOrThrowApiError<void>(res);
}

/**
 * Operation Package Instance Task フォーム保存・完了処理
 * セルフレポート代行処理用
 */
function saveFormOperationPackageInstanceTask(params: SaveFormOperationPackageInstanceTaskParams, payload: SaveFormOperationPackageInstanceTaskPayload): Promise<SaveFormOperationPackageInstanceTaskResponse> {
  const {
    instanceId,
    taskId
  } = params;
  return putOperationPackageInstanceTask('IT_OPERATION_CORE', instanceId, taskId, payload as unknown as OperationPackageInstanceTaskCompleteBody);
}